import React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';

const APPS = [
  {
    path: '/focuslounge',
    label: 'Focus Lounge',
    emoji: '🎯',
    description: 'Pomodoro timer, ambient themes, and music — focus in the browser',
  },
  {
    path: '/texteditor',
    label: 'Text Editor',
    emoji: '📝',
    description: 'Multi-tab code and text editor with syntax highlighting and text tools',
  },
  {
    path: '/text-cleaner',
    label: 'Text Cleaner',
    emoji: '✨',
    description: 'Clean pasted text — PDF wraps, spaces, invisible characters, presets',
  },
  {
    path: '/qrcodegenerator',
    label: 'QR Code',
    emoji: '⬛',
    description: 'QR codes for URLs, Wi-Fi, contacts, events, locations and more',
  },
  {
    path: '/appiconmaker',
    label: 'App Icon',
    emoji: '🎨',
    description: 'All required icon sizes for iOS, Android, macOS, watchOS and web',
  },
  {
    path: '/password-generator',
    label: 'Password Generator',
    emoji: '🔑',
    description: 'Strong passwords and passphrases — copy in one tap, fully local',
  },
  {
    path: '/imageresizer',
    label: 'Image Resizer',
    emoji: '🖼',
    description: 'Resize App Store and Google Play Store screenshots in bulk',
  },
  {
    path: '/image-marker',
    label: 'Image Marker',
    emoji: '🖍',
    description: 'Paste, annotate, and export images at full resolution in the browser',
  },
  {
    path: '/calculator',
    label: 'Calculator',
    emoji: '🔢',
    description: 'Client-side calculator with keyboard shortcuts and percent',
  },
  {
    path: '/tictactoe',
    label: 'Tic Tac Toe',
    emoji: '⭕',
    description: 'Solo or two-player 3×3 with adjustable AI — scores saved locally',
  },
];

const NotFound = () => {
  const { pathname } = useLocation();
  const cardBg    = useColorModeValue('white', 'gray.800');
  const border    = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box maxW="640px" mx="auto" pt={12} pb={16} px={4} textAlign="center">
      {/* 404 indicator */}
      <Text fontSize="6xl" fontWeight="extrabold" color="gray.200" lineHeight={1} mb={2}>
        404
      </Text>

      <Heading as="h1" size="lg" fontWeight="700" mb={2}>
        Page not found
      </Heading>

      <Text fontSize="sm" color={mutedText} mb={1}>
        The address{' '}
        <Text as="code" fontSize="sm" bg={useColorModeValue('gray.100','gray.700')} px={1.5} py={0.5} borderRadius="md">
          {pathname}
        </Text>{' '}
        no longer exists or has been moved.
      </Text>
      <Text fontSize="sm" color={mutedText} mb={10}>
        Here are all the tools you can use:
      </Text>

      {/* App cards */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} textAlign="left">
        {APPS.map((app) => (
          <Box
            key={app.path}
            as={Link}
            to={app.path}
            p={4}
            borderWidth="1px"
            borderColor={border}
            borderRadius="xl"
            bg={cardBg}
            shadow="xs"
            _hover={{ borderColor: 'blue.400', shadow: 'sm', transform: 'translateY(-1px)' }}
            _active={{ transform: 'translateY(0)' }}
            transition="all 0.15s"
            display="block"
          >
            <Text fontSize="2xl" mb={2}>{app.emoji}</Text>
            <Text fontWeight="semibold" fontSize="sm" mb={1}>{app.label}</Text>
            <Text fontSize="xs" color={mutedText}>{app.description}</Text>
          </Box>
        ))}
      </SimpleGrid>

      <Flex justify="center" mt={8}>
        <Button as={Link} to="/" colorScheme="blue" size="sm" borderRadius="lg">
          Go to Home
        </Button>
      </Flex>
    </Box>
  );
};

export default NotFound;
